import Button from '@mui/material/Button'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useRegisterSW } from 'virtual:pwa-register/react'

import messages from './messages.ts'

function ServiceWorker() {
  // check for updates every hour
  const period = 1000 * 10
  const { enqueueSnackbar } = useSnackbar()
  const { formatMessage } = useIntl()
  const {
    offlineReady: [offlineReady],
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      if (period <= 0) return

      if (r?.active?.state === 'activated') {
        registerPeriodicSync(period, swUrl, r)
      } else if (r?.installing) {
        r.installing.addEventListener('statechange', (e) => {
          const sw = e.target as ServiceWorker
          if (sw.state === 'activated') registerPeriodicSync(period, swUrl, r)
        })
      }
    },
  })

  const handleReload = useCallback(
    () => void updateServiceWorker(true),
    [updateServiceWorker]
  )

  useEffect(() => {
    if (offlineReady) {
      enqueueSnackbar(formatMessage(messages.offlineReady))
    }
    if (needRefresh) {
      enqueueSnackbar(formatMessage(messages.newVersionAvailable), {
        persist: true,
        action: (
          <Button onClick={handleReload}>
            {formatMessage(messages.reload)}
          </Button>
        ),
      })
    }
  }, [
    offlineReady,
    needRefresh,
    enqueueSnackbar,
    updateServiceWorker,
    formatMessage,
    handleReload,
  ])

  return null
}

export default ServiceWorker

/**
 * Periodically check for sw updates and update it to trigger a user info that a new version is available
 */
function registerPeriodicSync(
  period: number,
  swUrl: string,
  r: ServiceWorkerRegistration
) {
  if (period <= 0) return
  setInterval(async () => {
    if ('onLine' in navigator && !navigator.onLine) return

    const resp = await fetch(swUrl, {
      cache: 'no-store',
      headers: {
        cache: 'no-store',
        'cache-control': 'no-cache',
      },
    })

    if (resp?.status === 200) await r.update()
  }, period)
}
